import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Doughnut, HorizontalBar, Line } from "react-chartjs-2";
import { notification } from "antd";

import { fetchDashboardMeta, fetchDashboard, exportDashboard } from "./api";
import "./DashboardPage.css";

/* =====================================================================
 * DashboardPage — «Доходы и расходы» (исполнение бюджета).
 * Вёрстка и классы — точь-в-точь по макету v3. Данные — из API (агрегаты
 * summary / balance / income_categories / expense_groups / financing /
 * tree / dynamics), клиент их только отображает. Диаграммы — Chart.js 2.
 * ===================================================================== */

const TREE_SECTION = { inc: "INC", exp: "EXP", fin: "FIN" };

/* палитра категорий (нейтральная, не совпадает со статусами) */
const CAT_COLORS = [
  "#4f9fe0",
  "#a06fd6",
  "#3fa9b5",
  "#c78a3f",
  "#57c15d",
  "#6d7fd6",
];
const ST = { good: "#57c15d", warn: "#e0a63a", bad: "#e0605f", nd: "#8b95a8" };
const CAP_GRAY = "#3a4456";

/* ---------------------- форматирование / хелперы ---------------------- */
const fmtNum = (v, lang) =>
  v == null
    ? "—"
    : new Intl.NumberFormat(lang === "kk" ? "kk-KZ" : "ru-RU", {
        maximumFractionDigits: 1,
      }).format(v);

const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);
const stCls = (s) => (s ? `st-${s}` : "st-nd");
const stColor = (s) => ST[s] || ST.nd;
const nm = (o, lang) =>
  !o ? "" : lang === "kk" ? o.name_kk || o.name_ru : o.name_ru;

/* человекочитаемая подпись периода: "2026-year" → "2026", "2026-q2" → "2026 II кв." */
const periodText = (key, lang) => {
  if (!key) return "";
  const s = String(key);
  if (s.endsWith("-year")) return s.replace("-year", "");
  const q = s.match(/^(\d{4})-q([1-4])$/i);
  if (q)
    return `${q[1]} ${["I", "II", "III", "IV"][+q[2] - 1]} ${
      lang === "kk" ? "тоқ." : "кв."
    }`;
  return s;
};

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const lang = /^(kk|kz)/i.test(i18n.language || "") ? "kk" : "ru";

  const [meta, setMeta] = useState(undefined);
  const [metaError, setMetaError] = useState(false);

  const [budget, setBudget] = useState("consolidated");
  const [periodType, setPeriodType] = useState("year");
  const [report, setReport] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [treeTab, setTreeTab] = useState("inc");
  const [open, setOpen] = useState({});

  const [dash, setDash] = useState(undefined);
  const [dashError, setDashError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reqIdRef = useRef(0);

  const periods = useMemo(() => {
    if (!meta) return [];
    const src =
      periodType === "quarter" ? meta.periods.quarter : meta.periods.year;
    return src || [];
  }, [meta, periodType]);

  /* Подпись периода: label_ru/label_kk из меты бэка, иначе — из ключа. */
  const labelOf = useCallback(
    (key) => {
      const all = meta
        ? [...(meta.periods.year || []), ...(meta.periods.quarter || [])]
        : [];
      const p = all.find((x) => x.key === key);
      const lbl = p
        ? lang === "kk"
          ? p.label_kk || p.label_ru
          : p.label_ru
        : null;
      return lbl || periodText(key, lang);
    },
    [meta, lang],
  );

  const periodLabel = useCallback(
    (key) => {
      const p = periods.find((x) => x.key === key);
      const lbl = labelOf(key);
      if (!p || !p.is_partial) return lbl;
      const mark = t("budget.partialShort");
      // бэк может уже включить пометку в label — не дублируем
      return lbl.toLowerCase().includes(mark.toLowerCase())
        ? lbl
        : `${lbl} (${mark})`;
    },
    [periods, labelOf, t],
  );

  /* Мета — по одному бюджету: перезапрос при смене. Старая мета остаётся на
     экране до ответа (без мигания скелетона); _budget — чей это ответ. */
  useEffect(() => {
    let alive = true;
    setMetaError(false);
    fetchDashboardMeta(budget)
      .then((m) => alive && setMeta({ ...m, _budget: budget }))
      .catch(() => {
        if (!alive) return;
        setMeta(null);
        setMetaError(true);
      });
    return () => {
      alive = false;
    };
  }, [budget]);

  /* Выбранные периоды, которых нет в новой мете, → последний/первый доступный. */
  useEffect(() => {
    if (!meta) return;
    const src =
      periodType === "quarter" ? meta.periods.quarter : meta.periods.year;
    const repKeys = (src || []).map((p) => p.key);
    if (repKeys.indexOf(report) === -1)
      setReport(repKeys.length ? repKeys[repKeys.length - 1] : "");
    const keys = (meta.periods.year || []).map((p) => p.key);
    if (!keys.length) return;
    if (keys.indexOf(from) === -1) setFrom(keys[0]);
    if (keys.indexOf(to) === -1) setTo(keys[keys.length - 1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  const loadDashboard = useCallback(() => {
    // ждём мету своего бюджета — иначе период может быть от другого бюджета
    if (!meta || meta._budget !== budget || !report) return;
    const id = reqIdRef.current + 1;
    reqIdRef.current = id;
    setDashError(false);
    fetchDashboard({
      budget,
      periodType,
      report,
      from,
      to,
      treeSection: TREE_SECTION[treeTab],
      treeDepth: 5,
      lang,
    })
      .then((d) => reqIdRef.current === id && setDash(d))
      .catch(() => reqIdRef.current === id && setDashError(true));
  }, [meta, budget, periodType, report, from, to, treeTab, lang]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const onBudget = (v) => {
    setBudget(v);
    setOpen({});
  };
  const onPeriodType = (v) => {
    if (v === periodType) return;
    setPeriodType(v);
    const src = v === "quarter" ? meta.periods.quarter : meta.periods.year;
    const keys = (src || []).map((p) => p.key);
    setReport(keys.length ? keys[keys.length - 1] : "");
    setOpen({});
  };
  const onReport = (v) => {
    setReport(v);
    setOpen({});
  };
  const onTreeTab = (v) => {
    if (v !== treeTab) {
      setTreeTab(v);
      setOpen({});
    }
  };
  const onExport = (format) => {
    setExporting(true);
    Promise.resolve(
      exportDashboard(
        {
          budget,
          periodType,
          report,
          from,
          to,
          treeSection: TREE_SECTION[treeTab],
          lang,
        },
        format,
      ),
    )
      .catch(() =>
        notification.error({
          message: t("budget.expError"),
          placement: "bottomRight",
        }),
      )
      .finally(() => setExporting(false));
  };

  const rep = dash && dash.report ? dash.report : null;
  const snapCaption = useMemo(() => {
    if (!rep) return "";
    if (rep.is_partial && rep.date)
      return `· ${t("budget.onDate")} ${rep.date} · ${t("budget.partialLong")}`;
    return `· ${labelOf(rep.key)} · ${t("budget.fullYear")}`;
  }, [rep, t, labelOf]);

  const dynCaption = `· ${t("budget.rangeLbl")} ${labelOf(from)}–${labelOf(
    to,
  )}`;

  if (meta === undefined) return <Shell loadingMeta />;
  if (metaError)
    return (
      <Shell>
        <div className="bd-error">
          <span>{t("budget.loadError")}</span>
          <button className="bd-btn" onClick={() => window.location.reload()}>
            {t("budget.retry")}
          </button>
        </div>
      </Shell>
    );

  const isQuarterEmpty =
    periodType === "quarter" &&
    (!periods || !periods.length) &&
    meta.quarter_empty_reason;
  const partial = rep && rep.is_partial;

  return (
    <Shell>
      {/* ---------- блок управления ---------- */}
      <div className="period-controls">
        <div className="ctrl-group">
          <span className="ctrl-label">{t("budget.cBudget")}</span>
          <select
            className="ctrl-select"
            style={{ minWidth: 330 }}
            value={budget}
            onChange={(e) => onBudget(e.target.value)}
          >
            {meta.budgets.map((b) => (
              <option key={b.k} value={b.k}>
                {t(`budget.${b.label_key}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="ctrl-group">
          <span className="ctrl-label">{t("budget.cType")}</span>
          <div className="typeset">
            <button
              className={`typebtn${periodType === "year" ? " active" : ""}`}
              onClick={() => onPeriodType("year")}
            >
              {t("budget.tYear")}
            </button>
            <button
              className={`typebtn${periodType === "quarter" ? " active" : ""}`}
              onClick={() => onPeriodType("quarter")}
            >
              {t("budget.tQuarter")}
            </button>
          </div>
        </div>

        <div className="ctrl-group">
          <span className="ctrl-label">{t("budget.cReport")}</span>
          <select
            className="ctrl-select"
            value={report}
            onChange={(e) => onReport(e.target.value)}
            disabled={!periods.length}
          >
            {periods.map((p) => (
              <option key={p.key} value={p.key}>
                {periodLabel(p.key)}
              </option>
            ))}
          </select>
        </div>

        <div className="ctrl-group">
          <span className="ctrl-label">{t("budget.cRange")}</span>
          <select
            className="ctrl-select"
            style={{ minWidth: 110 }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          >
            {(meta.periods.year || []).map((p) => (
              <option key={p.key} value={p.key}>
                {labelOf(p.key)}
              </option>
            ))}
          </select>
          <span style={{ color: "var(--muted)" }}>—</span>
          <select
            className="ctrl-select"
            style={{ minWidth: 110 }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          >
            {(meta.periods.year || []).map((p) => (
              <option key={p.key} value={p.key}>
                {labelOf(p.key)}
              </option>
            ))}
          </select>
        </div>

        <div className="ctrl-group" style={{ marginLeft: "auto" }}>
          <span className="ctrl-label">{t("budget.cExport")}</span>
          <div className="typeset">
            <button
              className="typebtn"
              disabled={exporting}
              onClick={() => onExport("xlsx")}
            >
              {t("budget.expXlsx")}
            </button>
            <button
              className="typebtn"
              disabled={exporting}
              onClick={() => onExport("pdf")}
            >
              {t("budget.expPdf")}
            </button>
          </div>
        </div>
      </div>

      {partial && (
        <div className="warnbar">
          <span className="ic">⚠</span>
          <span>{t("budget.partialWarn", { d: rep.date })}</span>
        </div>
      )}

      {/* ---------- легенда ---------- */}
      <div className="legend">
        <span className="ctrl-label">{t("budget.legTitle")}</span>
        <span className="lg">
          <span className="dot good" />
          {t("budget.legInc")}
        </span>
        <span className="lg">
          <span className="dot warn" />
          {t("budget.legExp")}
        </span>
        <span className="lg">
          <span className="dot" style={{ background: "#8b95a8" }} />
          {t("budget.legNd")}
        </span>
        <span className="qnote">{t("budget.legQ")}</span>
      </div>

      {isQuarterEmpty ? null : dashError && dash === undefined ? (
        <div className="bd-error">
          <span>{t("budget.loadError")}</span>
          <button className="bd-btn" onClick={loadDashboard}>
            {t("budget.retry")}
          </button>
        </div>
      ) : (
        <>
          {dashError && dash !== undefined && (
            <div className="bd-error" style={{ marginBottom: 16 }}>
              <span>{t("budget.loadError")}</span>
              <button className="bd-btn" onClick={loadDashboard}>
                {t("budget.retry")}
              </button>
            </div>
          )}

          <Section title={t("budget.sSum")} caption={snapCaption}>
            {renderSummary(dash, t, lang)}
          </Section>
          <Section title={t("budget.sBal")} caption={snapCaption}>
            {renderBalance(dash, t, lang)}
          </Section>
          <Section title={t("budget.sInc")} caption={snapCaption} cls="inc">
            {renderIncome(dash, t, lang)}
          </Section>
          <Section title={t("budget.sExp")} caption={snapCaption} cls="exp">
            {renderExpense(dash, t, lang)}
          </Section>
          <Section title={t("budget.sFin")} caption={snapCaption}>
            {renderFinancing(dash, t, lang)}
          </Section>
          <Section
            title={t("budget.sTree")}
            caption={snapCaption}
            actions={
              <div className="tabset">
                {["inc", "exp", "fin"].map((k) => (
                  <button
                    key={k}
                    className={`tabbtn${treeTab === k ? " active" : ""}`}
                    onClick={() => onTreeTab(k)}
                  >
                    {t(`budget.tab${k[0].toUpperCase()}${k.slice(1)}`)}
                  </button>
                ))}
              </div>
            }
          >
            {renderTree(dash, t, lang, open, setOpen)}
          </Section>
          <Section title={t("budget.sDyn")} caption={dynCaption}>
            {renderDynamics(dash, t, lang)}
          </Section>
        </>
      )}
    </Shell>
  );
}

/* ============================ секции ============================ */

/* 1. Сводка — 4 карточки .bk */
function renderSummary(dash, t, lang) {
  if (dash === undefined) return <Skel />;
  const s = dash.summary;
  if (!s) return <div className="bd-body-stub">{t("budget.sectionSoon")}</div>;
  const n = (v) => fmtNum(v, lang);
  const defCard = s.deficit && s.deficit.is_deficit;
  const bal = s.balances || {};
  return (
    <>
      <div className="bkpis">
        <div className="bk inc">
          <div className="cap">{t("budget.sumIncome")}</div>
          <div className={`num ${stCls(s.income.status)}`}>
            {n(s.income.fact)}
          </div>
          <div className="sub">
            {t("budget.plan")}: <b>{n(s.income.plan)}</b>
            <br />
            {t("budget.execToPlan")}: <b>{fmtPct(s.income.exec_to_plan_pct)}</b>{" "}
            · {t("budget.execToBud")}: <b>{fmtPct(s.income.exec_to_bud_pct)}</b>
          </div>
          <div
            className="bar"
            style={{
              width: `${Math.min(s.income.exec_to_plan_pct || 0, 100)}%`,
              background: "var(--inc)",
            }}
          />
        </div>

        <div className="bk exp">
          <div className="cap">{t("budget.sumExpense")}</div>
          <div className={`num ${stCls(s.expense.status)}`}>
            {n(s.expense.fact)}
          </div>
          <div className="sub">
            {t("budget.plan")}: <b>{n(s.expense.plan)}</b> ·{" "}
            {t("budget.underspent")}: <b>{n(s.expense.underspent)}</b>
            <br />
            {t("budget.execToPlan")}:{" "}
            <b>{fmtPct(s.expense.exec_to_plan_pct)}</b> ·{" "}
            {t("budget.execToBud")}: <b>{fmtPct(s.expense.exec_to_bud_pct)}</b>
          </div>
          <div
            className="bar"
            style={{
              width: `${Math.min(s.expense.exec_to_plan_pct || 0, 100)}%`,
              background: "var(--exp)",
            }}
          />
        </div>

        <div className="bk">
          <div className="cap">
            {defCard ? t("budget.deficit") : t("budget.surplus")}
          </div>
          <div className={`num ${defCard ? "bad-c" : "good-c"}`}>
            {n(s.deficit.fact)}
          </div>
          <div className="sub">
            {t("budget.plan")}: <b>{n(s.deficit.plan)}</b>
          </div>
          <div
            className="bar"
            style={{ background: defCard ? "var(--bad)" : "var(--good)" }}
          />
        </div>

        <div className="bk">
          <div className="cap">{t("budget.balEnd")}</div>
          <div className="num">{n(bal.end)}</div>
          <div className="sub">
            {t("budget.balBegin")}: <b>{n(bal.begin)}</b>
            <br />
            {t("budget.balUsed")}: <b>{n(bal.used)}</b>
          </div>
          <div className="bar" style={{ background: "var(--brand)" }} />
        </div>
      </div>
      <div className="kpinote">{t("budget.sumNote")}</div>
    </>
  );
}

/* 2. Баланс ресурсов и использования */
function renderBalance(dash, t, lang) {
  if (dash === undefined) return <Skel />;
  const b = dash.balance;
  if (!b) return <div className="bd-body-stub">{t("budget.sectionSoon")}</div>;
  const col = (rows, headKey, totalVal, cls) => (
    <div className={`balcol ${cls}`}>
      <div className="bh">
        <span>{t(headKey)}</span>
      </div>
      {(rows || []).map((r, i) => (
        <div className={`balrow${r.marked ? " mark" : ""}`} key={r.section + i}>
          <span className="ix">{i + 1}</span>
          <span className="lbl">
            {nm(r, lang)}
            {r.marked ? (
              <span className="markbadge">{t("budget.addedBadge")}</span>
            ) : null}
          </span>
          <span className="v">{fmtNum(r.value, lang)}</span>
        </div>
      ))}
      <div className="baltot">
        <span>
          {t(cls === "res" ? "budget.balResTotal" : "budget.balUseTotal")}
        </span>
        <span className="v">{fmtNum(totalVal, lang)}</span>
      </div>
    </div>
  );
  return (
    <>
      <div className="balgrid">
        {col(b.resources, "budget.colRes", b.resources_total, "res")}
        <div className="baleq">=</div>
        {col(b.uses, "budget.colUse", b.uses_total, "use")}
      </div>
      <div className={`balcheck${b.balanced ? "" : " err"}`}>
        <span className="tag">
          {b.balanced ? t("budget.balOk") : t("budget.balErr")}
        </span>
        <span>
          {t("budget.residualLbl")}: {fmtNum(b.residual, lang)} ·{" "}
          {t("budget.toleranceLbl")}: {fmtNum(b.tolerance, lang)}
        </span>
      </div>
    </>
  );
}

/* 3. Доходы — кольцевая + карточки категорий */
function renderIncome(dash, t, lang) {
  if (dash === undefined) return <Skel />;
  const cats = dash.income_categories || [];
  if (!cats.length)
    return <div className="bd-body-stub">{t("budget.noData")}</div>;

  const data = {
    labels: cats.map((c) => nm(c, lang)),
    datasets: [
      {
        data: cats.map((c) => c.fact),
        backgroundColor: cats.map((_, i) => CAT_COLORS[i % CAT_COLORS.length]),
        borderColor: "#1e2532",
        borderWidth: 2,
      },
    ],
  };
  const total = cats.reduce((a, c) => a + (c.fact || 0), 0);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 55,
    legend: {
      display: true,
      position: "bottom",
      labels: { fontColor: "#c7ced9", boxWidth: 12, fontSize: 11 },
    },
    tooltips: {
      callbacks: {
        label: (it, d) => {
          const v = d.datasets[0].data[it.index];
          return `${d.labels[it.index]}: ${fmtNum(v, lang)} (${
            total ? Math.round((v / total) * 100) : 0
          }%)`;
        },
      },
    },
  };

  return (
    <div className="cols2">
      <div className="chart-box h-inc">
        <Doughnut key={"inc" + cats.length} data={data} options={options} />
      </div>
      <div className="cat-grid">
        {cats.map((c, i) => {
          const color = CAT_COLORS[i % CAT_COLORS.length];
          const done = Math.min(c.fact || 0, c.plan || 0);
          const rest = Math.max((c.plan || 0) - (c.fact || 0), 0);
          const ring = {
            datasets: [
              {
                data: [done, rest],
                backgroundColor: [stColor(c.status), "#2b3446"],
                borderWidth: 0,
              },
            ],
          };
          const ringOpt = {
            responsive: true,
            maintainAspectRatio: false,
            cutoutPercentage: 70,
            legend: { display: false },
            tooltips: { enabled: false },
          };
          return (
            <div className="cat" key={c.code}>
              <div className="mini">
                <Doughnut
                  key={"m" + c.code + (c.fact || 0)}
                  data={ring}
                  options={ringOpt}
                />
                <span className="mini-c">{fmtPct(c.exec_to_plan_pct)}</span>
              </div>
              <div>
                <div className="nm">
                  <span className="cdot" style={{ background: color }} />
                  {nm(c, lang)}
                </div>
                <div className="big">{fmtNum(c.fact, lang)}</div>
                <div className="sub">
                  {t("budget.plan")}: {fmtNum(c.plan, lang)} ·{" "}
                  {t("budget.share")}: {fmtPct(c.share_pct)}
                  {"  "}
                  <span className={`pill ${c.status || "nd"}`}>
                    {fmtPct(c.exec_to_plan_pct)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* 4. Расходы — горизонтальная столбчатая + таблица */
function renderExpense(dash, t, lang) {
  if (dash === undefined) return <Skel />;
  const rows = dash.expense_groups || [];
  if (!rows.length)
    return <div className="bd-body-stub">{t("budget.noData")}</div>;

  const data = {
    labels: rows.map((r) => nm(r, lang)),
    datasets: [
      {
        label: t("budget.plan"),
        data: rows.map((r) => r.plan),
        backgroundColor: CAP_GRAY,
        barPercentage: 0.9,
        categoryPercentage: 0.72,
      },
      {
        label: t("budget.fact"),
        data: rows.map((r) => r.fact),
        backgroundColor: rows.map((r) => stColor(r.status)),
        barPercentage: 0.9,
        categoryPercentage: 0.72,
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      xAxes: [
        {
          gridLines: { color: "rgba(139,149,168,.12)" },
          ticks: { fontColor: "#8b95a8", beginAtZero: true },
        },
      ],
      yAxes: [
        {
          gridLines: { display: false },
          ticks: {
            fontColor: "#c7ced9",
            fontSize: 11,
            autoSkip: false,
            callback: (l) => (l.length > 38 ? l.slice(0, 38) + "…" : l),
          },
        },
      ],
    },
    tooltips: {
      callbacks: {
        title: (its) => nm(rows[its[0].index], lang),
        label: (it, d) =>
          `${d.datasets[it.datasetIndex].label}: ${fmtNum(
            d.datasets[it.datasetIndex].data[it.index],
            lang,
          )}`,
      },
    },
  };

  return (
    <div className="cols2">
      <div className="chart-box h-exp">
        <HorizontalBar
          key={"exp" + rows.length}
          data={data}
          options={options}
        />
      </div>
      <div style={{ overflow: "auto", maxHeight: 460 }}>
        <table>
          <thead>
            <tr>
              <th>{t("budget.thGroup")}</th>
              <th className="n">{t("budget.plan")}</th>
              <th className="n">{t("budget.fact")}</th>
              <th className="n">{t("budget.thExec")}</th>
              <th className="n">{t("budget.share")}</th>
              <th className="n">{t("budget.underspent")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td>
                  <span className={`st-dot ${r.status || "nd"}`} />
                  {r.code} {nm(r, lang)}
                </td>
                <td className="n">{fmtNum(r.plan, lang)}</td>
                <td className="n">{fmtNum(r.fact, lang)}</td>
                <td className="n">{fmtPct(r.exec_to_plan_pct)}</td>
                <td className="n">{fmtPct(r.share_pct)}</td>
                <td className="n">{fmtNum(r.underspent, lang)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* 5. Финансирование дефицита и остатки */
function renderFinancing(dash, t, lang) {
  if (dash === undefined) return <Skel />;
  const rows = dash.financing || [];
  const bal = (dash.summary && dash.summary.balances) || {};
  if (!rows.length)
    return <div className="bd-body-stub">{t("budget.sectionSoon")}</div>;
  const flow = (capKey, v) => (
    <div className="flowcard">
      <div className="cap">{t(capKey)}</div>
      <div className="num">{fmtNum(v, lang)}</div>
    </div>
  );
  return (
    <>
      <div className="finflow">
        {flow("budget.balBegin", bal.begin)}
        {flow("budget.balUsed", bal.used)}
        {flow("budget.balEnd", bal.end)}
      </div>
      <div className="finnote">{t("budget.finNote")}</div>
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>{t("budget.thSection")}</th>
              <th className="n">{t("budget.thApproved")}</th>
              <th className="n">{t("budget.thUpdated")}</th>
              <th className="n">{t("budget.thAdjusted")}</th>
              <th className="n">{t("budget.plan")}</th>
              <th className="n">{t("budget.fact")}</th>
              <th className="n">{t("budget.thExec")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.section + i}
                className={r.level === 0 ? "fin-l0" : "fin-l1"}
              >
                <td>{nm(r, lang)}</td>
                <td className="n">{fmtNum(r.approved, lang)}</td>
                <td className="n">{fmtNum(r.updated, lang)}</td>
                <td className="n">{fmtNum(r.adjusted, lang)}</td>
                <td className="n">{fmtNum(r.plan, lang)}</td>
                <td className="n">{fmtNum(r.fact, lang)}</td>
                <td className="n">{fmtPct(r.exec_to_plan_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* 6. Дерево бюджетной классификации (сворачиваемое) */
function renderTree(dash, t, lang, open, setOpen) {
  if (dash === undefined) return <Skel />;
  const tree = dash.tree || [];
  if (!tree.length)
    return <div className="bd-body-stub">{t("budget.noData")}</div>;

  const byParent = {};
  tree.forEach((n) => {
    const p = n.parent_id == null ? "ROOT" : n.parent_id;
    (byParent[p] = byParent[p] || []).push(n);
  });
  const visible = [];
  (function walk(pid) {
    (byParent[pid] || []).forEach((n) => {
      visible.push(n);
      if (n.has_children && open[n.node_id]) walk(n.node_id);
    });
  })("ROOT");

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div style={{ overflow: "auto", maxHeight: 620 }}>
      <table>
        <thead>
          <tr>
            <th>{t("budget.thName")}</th>
            <th className="n">{t("budget.thApproved")}</th>
            <th className="n">{t("budget.thUpdated")}</th>
            <th className="n">{t("budget.thAdjusted")}</th>
            <th className="n">{t("budget.plan")}</th>
            <th className="n">{t("budget.fact")}</th>
            <th className="n">{t("budget.thExec")}</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((n) => (
            <tr key={n.node_id} className={n.has_children ? "expandable" : ""}>
              <td>
                <span
                  style={{
                    paddingLeft: (n.depth - 1) * 16,
                    display: "inline-block",
                  }}
                >
                  <span
                    className="twist"
                    onClick={() => n.has_children && toggle(n.node_id)}
                  >
                    {n.has_children ? (open[n.node_id] ? "▾" : "▸") : ""}
                  </span>
                  <span
                    className="st-dot"
                    style={{ background: stColor(n.status) }}
                  />
                  <span
                    className="tname"
                    onClick={() => n.has_children && toggle(n.node_id)}
                  >
                    {n.code} {nm(n, lang)}
                  </span>
                </span>
              </td>
              <td className="n">{fmtNum(n.approved, lang)}</td>
              <td className="n">{fmtNum(n.updated, lang)}</td>
              <td className="n">{fmtNum(n.adjusted, lang)}</td>
              <td className="n">{fmtNum(n.plan, lang)}</td>
              <td className="n">{fmtNum(n.fact, lang)}</td>
              <td className="n">{fmtPct(n.exec_to_plan_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* 7. Динамика по периодам */
function renderDynamics(dash, t, lang) {
  if (dash === undefined) return <Skel />;
  const dyn = dash.dynamics;
  if (!dyn || !dyn.periods || !dyn.periods.length)
    return <div className="bd-body-stub">{t("budget.noData")}</div>;
  const labels = dyn.periods.map(
    (p) => (lang === "kk" ? p.label_kk || p.label_ru : p.label_ru) || p.key,
  );
  const s = dyn.series || {};
  const mk = (arr, color, label) => ({
    label,
    data: arr || [],
    borderColor: color,
    backgroundColor: color,
    fill: false,
    tension: 0.2,
    pointRadius: 3,
    borderWidth: 2,
  });
  const data = {
    labels,
    datasets: [
      mk(s.income, "#4f9fe0", t("budget.sumIncome")),
      mk(s.expense, "#e0a63a", t("budget.sumExpense")),
      mk(s.deficit, "#e0605f", t("budget.deficit")),
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: "bottom",
      labels: { fontColor: "#c7ced9", boxWidth: 14, fontSize: 12 },
    },
    scales: {
      xAxes: [
        {
          gridLines: { color: "rgba(139,149,168,.10)" },
          ticks: { fontColor: "#8b95a8" },
        },
      ],
      yAxes: [
        {
          gridLines: { color: "rgba(139,149,168,.10)" },
          ticks: { fontColor: "#8b95a8" },
        },
      ],
    },
    tooltips: {
      mode: "index",
      intersect: false,
      callbacks: {
        label: (it, d) =>
          `${d.datasets[it.datasetIndex].label}: ${fmtNum(it.yLabel, lang)}`,
      },
    },
  };
  return (
    <>
      <div className="chart-box h-dyn">
        <Line key={"dyn" + labels.length} data={data} options={options} />
      </div>
      <div className="kpinote">{t("budget.dynNote")}</div>
      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <table>
          <thead>
            <tr>
              <th>{t("budget.thPeriod")}</th>
              <th className="n">{t("budget.sumIncome")}</th>
              <th className="n">{t("budget.sumExpense")}</th>
              <th className="n">{t("budget.deficit")}</th>
            </tr>
          </thead>
          <tbody>
            {labels.map((lb, i) => (
              <tr key={lb + i}>
                <td>{lb}</td>
                <td className="n">{fmtNum((s.income || [])[i], lang)}</td>
                <td className="n">{fmtNum((s.expense || [])[i], lang)}</td>
                <td className="n">{fmtNum((s.deficit || [])[i], lang)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ============================ каркас ============================ */
function Shell({ children, loadingMeta }) {
  return (
    <div className="bdx">
      <div className="wrap">
        {loadingMeta ? (
          <>
            <div className="bd-skel bd-skel-panel" />
            <div className="bd-skel bd-skel-panel" />
          </>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function Section({ title, caption, cls, actions, children }) {
  return (
    <div className="section">
      <div className={`section-head${cls ? " " + cls : ""}`}>
        <span>{title}</span>
        {caption ? <span className="hp">{caption}</span> : null}
        {actions || null}
      </div>
      <div className="section-body">{children}</div>
    </div>
  );
}

function Skel() {
  return (
    <div>
      <div className="bd-skel bd-skel-line" style={{ width: "60%" }} />
      <div className="bd-skel bd-skel-line" style={{ width: "90%" }} />
      <div className="bd-skel bd-skel-line" style={{ width: "75%" }} />
    </div>
  );
}
